import React, { createContext, useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as api from '../config/api';
import { useLocation } from 'react-router-dom';
import { DEFAULT_TRACK } from '../types/constants';
import { datadogRum } from '@datadog/browser-rum';

// Import the ActivityType type
type ActivityType = 'learning' | 'accuracyPractice' | 'fluencyPractice' | 'assessment' | 'other';
type PageType = 'dashboard' | 'learn' | 'practice' | 'timed-practice' | 'accuracy-practice' | 'fluency-practice' | 'assessment' | 'onboarding' | 'prek-practice' | 'k-practice' | 'other';

// Define the FactsByStage type to match the backend
type FactsByStage = {
  learning?: string[];
  accuracyPractice?: string[];
  fluency6Practice?: string[];
  fluency3Practice?: string[];
  fluency2Practice?: string[];
  fluency1_5Practice?: string[];
  fluency1Practice?: string[];
};

interface SessionContextType {
  recordPageTransition: (page: PageType, factsByStage?: FactsByStage) => void;
  isSessionActive: boolean;
}

export const SessionContext = createContext<SessionContextType>({
  recordPageTransition: () => {},
  isSessionActive: true
});

// Additional window to treat same-page transitions as duplicates (ms)
const DUPLICATE_WINDOW_MS = 5000; // 5 seconds – must match backend logic

// Minimum time (in ms) between recording page transitions to prevent excessive API calls
const MIN_TRANSITION_INTERVAL = 500;
// Maximum retry attempts for failed API calls
const MAX_RETRY_ATTEMPTS = 3;
// Delay between retry attempts (in ms)
const RETRY_DELAY = 1000;

// Helper function to map route paths to page types
const getPageTypeFromPath = (path: string): PageType => {
  if (path === '/' || path.startsWith('/dashboard')) return 'dashboard';
  if (path === '/learn') return 'learn';
  if (path === '/practice') return 'practice';  // Keep as distinct page type
  if (path === '/timedpractice') return 'timed-practice';  // Keep as distinct page type
  if (path.startsWith('/accuracy-practice')) return 'accuracy-practice';
  if (path.startsWith('/fluency-practice')) return 'fluency-practice';
  // Treat progress assessment and onboarding assessment pages as assessment activities
  if (path.startsWith('/progress-assessment')) return 'assessment';
  if (path.startsWith('/onboarding')) return 'onboarding';
  if (path.startsWith('/pre-k-practice')) return 'prek-practice';
  if (path.startsWith('/k-practice')) return 'k-practice';
  if (path.startsWith('/k-accuracy-assessment')) return 'assessment';
  return 'other';
};

export const SessionProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Helper to get current track ID just-in-time
  const getCurrentTrackId = useCallback(() => {
    return sessionStorage.getItem('activeTrackId') || DEFAULT_TRACK;
  }, []);
  
  // Ref to track the last transition time to implement debouncing
  const lastTransitionTimeRef = useRef<number>(0);
  // Ref to track the last recorded page to prevent duplicates
  const lastPageRef = useRef<PageType | null>(null);
  // Ref to track the last successfully ACCEPTED transition time (used for duplicate suppression)
  const lastAcceptedTimeRef = useRef<number>(0);

  // Function to record transition with retry logic
  const recordTransitionWithRetry = useCallback(async (
    userId: string,
    currentTrackId: string,
    page: PageType,
    factsByStage?: FactsByStage,
    attempt: number = 1
  ) => {
    try {
      const now = Date.now();

      // 1. Treat very rapid duplicate transitions (same page, no new facts) within DUPLICATE_WINDOW_MS as duplicates
      if (
        lastPageRef.current === page &&
        !factsByStage &&
        now - lastAcceptedTimeRef.current < DUPLICATE_WINDOW_MS
      ) {
        datadogRum.addAction('page_transition_skipped', {
          userId,
          trackId: currentTrackId,
          page,
          reason: 'duplicate_within_window',
          timeSinceLastAccepted: now - lastAcceptedTimeRef.current
        });
        return;
      }

      // 2. Throttle *identical* page transitions that occur too quickly (< MIN_TRANSITION_INTERVAL)
      if (
        lastPageRef.current === page &&
        now - lastTransitionTimeRef.current < MIN_TRANSITION_INTERVAL
      ) {
        datadogRum.addAction('page_transition_skipped', {
          userId,
          trackId: currentTrackId,
          page,
          reason: 'throttled',
          timeSinceLastTransition: now - lastTransitionTimeRef.current
        });
        return;
      }

      // Capture previous page before updating ref
      const prevPage = lastPageRef.current;

      // Update timing refs for this attempt
      lastTransitionTimeRef.current = now;
      lastPageRef.current = page;

      // Log transition attempt
      datadogRum.addAction('page_transition_sending', {
        userId,
        trackId: currentTrackId,
        page,
        attempt,
        lastPage: prevPage
      });

      const response = await api.recordPageTransition({
        userId,
        trackId: currentTrackId,
        page: page,
        factsByStage
      });

      if (response.success) {
        lastAcceptedTimeRef.current = now;
        datadogRum.addAction('page_transition_success', {
          userId,
          trackId: currentTrackId,
          page,
          attempt
        });
      } else if (attempt < MAX_RETRY_ATTEMPTS) {
        // Wait and retry with exponential backoff
        setTimeout(() => {
          recordTransitionWithRetry(userId, currentTrackId, page, factsByStage, attempt + 1);
        }, RETRY_DELAY * Math.pow(2, attempt - 1));
      }
    } catch (error) {
      console.error('Failed to record page transition:', error);
      datadogRum.addAction('page_transition_error', {
        userId,
        trackId: currentTrackId,
        page,
        attempt,
        error: error instanceof Error ? error.message : String(error)
      });

      if (attempt < MAX_RETRY_ATTEMPTS) {
        // Wait and retry with exponential backoff
        setTimeout(() => {
          recordTransitionWithRetry(userId, currentTrackId, page, factsByStage, attempt + 1);
        }, RETRY_DELAY * Math.pow(2, attempt - 1));
      }
    }
  }, []);
  
  // Record page transition when location changes
  useEffect(() => {
    if (user?.userId) {
      // Skip PreK/K assessments - handled by component directly with correct trackId
      if (location.pathname.startsWith('/pre-k-assessment')) return;
      if (location.pathname.startsWith('/k-assessment')) return;

      const pageType = getPageTypeFromPath(location.pathname);
      recordTransitionWithRetry(user.userId, getCurrentTrackId(), pageType);
    }
  }, [location.pathname, user?.userId, recordTransitionWithRetry, getCurrentTrackId]);
  
  // Public function for components to record transitions with fact data
  const recordPageTransition = useCallback((page: PageType, factsByStage?: FactsByStage) => {
    if (!user?.userId) return;
    recordTransitionWithRetry(user.userId, getCurrentTrackId(), page, factsByStage);
  }, [user, recordTransitionWithRetry, getCurrentTrackId]);
  
  return (
    <SessionContext.Provider value={{ recordPageTransition, isSessionActive: true }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext); 